import { Button, FormField, Notice, Text } from "@smooth/design-system-components"
import { isAxiosError } from "axios"
import { useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"

const ENDPOINT = `${SMOOTH_SMOOTHER_API_URL}/screen/factoring_csv`

interface Validation {
  recordCount: number
  warnings: string[]
  errors: string[]
  /** 検証で一時保存した CSV。エラーがあるときは返らず、実行できない */
  tmpS3FileName: string | null
}

const messageOf = (e: unknown, fallback: string) =>
  isAxiosError(e) && typeof e.response?.data?.message === "string" ? e.response.data.message : fallback

const MessageList = ({ messages }: { messages: string[] }) => (
  <ul className="flex max-h-[300px] list-disc flex-col gap-1 overflow-y-auto pl-6">
    {messages.map((message, index) => (
      <li key={index} className="whitespace-pre-line break-all">
        <Text size="sm" as="span">
          {message}
        </Text>
      </li>
    ))}
  </ul>
)

/**
 * ファクタリング CSV。admin と同じく、CSV を選んで検証し、エラーが無ければ実行する 2 段階。
 * 実行は、検証で一時保存した CSV だけを対象にする。
 */
export const FactoringCsv = () => {
  const { postWithToken } = useSmoothApi()
  const [file, setFile] = useState<File | null>(null)
  const [validation, setValidation] = useState<Validation | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: "info" | "error"; message: string } | null>(null)

  const handleValidate = async () => {
    if (!file) return
    setIsBusy(true)
    setValidation(null)
    setNotice(null)
    try {
      const form = new FormData()
      form.append("csvFile", file)
      const response = await postWithToken(`${ENDPOINT}/validate`, form)
      setValidation(response.data as Validation)
    } catch (e) {
      setNotice({ tone: "error", message: messageOf(e, "CSVの解析に失敗しました") })
    } finally {
      setIsBusy(false)
    }
  }

  const handleExecute = async () => {
    if (!validation?.tmpS3FileName) return
    if (!window.confirm(`${validation.recordCount}件の債権の所有者の移転を作ります。よろしいですか？`)) return
    setIsBusy(true)
    setNotice(null)
    try {
      await postWithToken(`${ENDPOINT}/execute`, { tmpS3FileName: validation.tmpS3FileName }, { "axios-retry": { retries: 0 } })
      setNotice({ tone: "info", message: "アップロードが無事完了しました" })
      setValidation(null)
      setFile(null)
    } catch (e) {
      setNotice({ tone: "error", message: messageOf(e, "データ作成に失敗しました") })
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {notice && (
        <Notice tone={notice.tone} variant="card">
          {notice.message}
        </Notice>
      )}
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          handleValidate()
        }}
      >
        <FormField label="CSVファイルをアップロード" hint="契約 ID・移転日・新所有者（元本）・新所有者（手数料）の列">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null)
              setValidation(null)
            }}
          />
        </FormField>
        <div>
          <Button type="submit" disabled={isBusy || !file}>
            検証する
          </Button>
        </div>
      </form>

      {validation && (
        <section className="flex flex-col gap-4">
          <Text>{validation.recordCount}件のデータが読み込まれました</Text>
          {validation.warnings.length > 0 && (
            <Notice tone="warning" variant="card">
              <Text as="p">こちらの内容はエラーではないですが、注意が必要です</Text>
              <MessageList messages={validation.warnings} />
            </Notice>
          )}
          {validation.errors.length > 0 ? (
            <Notice tone="error" variant="card">
              <Text as="p">以下のエラーが発生しました。CSV を直してから、もう一度検証してください</Text>
              <MessageList messages={validation.errors} />
            </Notice>
          ) : (
            <div>
              <Button disabled={isBusy || !validation.tmpS3FileName} onClick={handleExecute}>
                実行する
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
